import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { generateDocumentNumber, getDocumentBookKey, normalizeDocumentDate } from '../utils';
import type { UseDocumentNumberProps, UseDocumentNumberReturn } from '../types';
import type { QuyenChungTu, QuyenChungTuResponse } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

export const useDocumentNumberV2 = ({
  ma_ct,
  dateFieldName = 'ngay_ct',
  initialQuyenChungTu = null,
  initialSoChungTu = '',
  formMode = 'add'
}: UseDocumentNumberProps): UseDocumentNumberReturn => {
  const formContext = useFormContext();
  const { entity } = useAuth();
  const documentDate = normalizeDocumentDate(formContext?.watch?.(dateFieldName));
  const [quyenChungTus, setQuyenChungTus] = useState<QuyenChungTu[]>([]);
  const [quyenChungTu, setSelectedQuyenChungTu] = useState<QuyenChungTu | null>(initialQuyenChungTu);
  const [soChungTu, setSoChungTu] = useState(initialSoChungTu);
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestRef = useRef(0);
  const previousDocumentDateRef = useRef(documentDate);
  const pendingDateRegenerateRef = useRef(false);
  const fetchedDocumentDateRef = useRef<string | null>(null);

  const initialQuyenChungTuKey = getDocumentBookKey(initialQuyenChungTu);

  useEffect(() => {
    setSoChungTu(prevValue => (prevValue === initialSoChungTu ? prevValue : initialSoChungTu));
  }, [initialSoChungTu]);

  useEffect(() => {
    setSelectedQuyenChungTu(prevValue =>
      getDocumentBookKey(prevValue) === initialQuyenChungTuKey ? prevValue : initialQuyenChungTu
    );
  }, [initialQuyenChungTu, initialQuyenChungTuKey]);

  const fetchQuyenChungTus = useCallback(async () => {
    if (!entity?.slug || !ma_ct) {
      setQuyenChungTus([]);
      setIsLoading(false);
      fetchedDocumentDateRef.current = documentDate;
      return;
    }

    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setIsLoading(true);

    try {
      const queryParams = new URLSearchParams({
        ma_ct,
        ngay_hl: documentDate
      });
      const response = await api.get<QuyenChungTuResponse>(
        `/entities/${entity.slug}/erp/document-books/by-chung-tu/?${queryParams.toString()}`
      );

      if (latestRequestRef.current === requestId) {
        fetchedDocumentDateRef.current = documentDate;
        setQuyenChungTus(response.data.results || []);
      }
    } catch (error) {
      if (latestRequestRef.current === requestId) {
        setQuyenChungTus([]);
      }
      console.error('Error fetching document books by document code:', error);
    } finally {
      if (latestRequestRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [documentDate, entity?.slug, ma_ct]);

  useEffect(() => {
    void fetchQuyenChungTus();
  }, [fetchQuyenChungTus]);

  useEffect(() => {
    if (previousDocumentDateRef.current === documentDate) {
      return;
    }

    previousDocumentDateRef.current = documentDate;
    pendingDateRegenerateRef.current = true;
  }, [documentDate]);

  useEffect(() => {
    const selectedKey = getDocumentBookKey(quyenChungTu);
    if (!selectedKey) {
      return;
    }

    const refreshedQuyenChungTu = quyenChungTus.find(item => getDocumentBookKey(item) === selectedKey);
    if (refreshedQuyenChungTu && refreshedQuyenChungTu !== quyenChungTu) {
      setSelectedQuyenChungTu(refreshedQuyenChungTu);
    }
  }, [quyenChungTu, quyenChungTus]);

  const setQuyenChungTu = useCallback(
    (nextQuyenChungTu: QuyenChungTu | null) => {
      setSelectedQuyenChungTu(prevValue =>
        getDocumentBookKey(prevValue) === getDocumentBookKey(nextQuyenChungTu) ? prevValue : nextQuyenChungTu
      );

      if (!nextQuyenChungTu) {
        return soChungTu;
      }

      const nextSoChungTu = generateDocumentNumber(nextQuyenChungTu, documentDate);
      setSoChungTu(prevValue => (prevValue === nextSoChungTu ? prevValue : nextSoChungTu));
      return nextSoChungTu;
    },
    [documentDate, soChungTu]
  );

  useEffect(() => {
    if (
      formMode !== 'add' ||
      !pendingDateRegenerateRef.current ||
      isLoading ||
      !quyenChungTu ||
      fetchedDocumentDateRef.current !== documentDate
    ) {
      return;
    }

    const selectedKey = getDocumentBookKey(quyenChungTu);
    const refreshedQuyenChungTu = quyenChungTus.find(item => getDocumentBookKey(item) === selectedKey);

    if (selectedKey && quyenChungTus.length > 0 && !refreshedQuyenChungTu) {
      return;
    }

    const sourceQuyenChungTu = refreshedQuyenChungTu || quyenChungTu;
    const nextSoChungTu = generateDocumentNumber(sourceQuyenChungTu, documentDate);

    pendingDateRegenerateRef.current = false;
    setSoChungTu(prevValue => (prevValue === nextSoChungTu ? prevValue : nextSoChungTu));
  }, [documentDate, formMode, isLoading, quyenChungTu, quyenChungTus]);

  return useMemo(
    () => ({
      quyenChungTu,
      setQuyenChungTu,
      soChungTu,
      setSoChungTu,
      quyenChungTus,
      isLoading,
      refreshQuyenChungTus: fetchQuyenChungTus
    }),
    [fetchQuyenChungTus, isLoading, quyenChungTu, quyenChungTus, setQuyenChungTu, soChungTu]
  );
};
